import { useEffect, useLayoutEffect, useRef } from "react";
import { UAParser } from "ua-parser-js";

// --- 1. Module-level instantiation ---
// We instantiate UAParser once here so we don't create overhead on every render.
const parser = new UAParser();
const browserResult = parser.getBrowser();
// Safe check for SSR and browser name
const isSafari =
  typeof window !== "undefined" &&
  browserResult.name &&
  browserResult.name.includes("Safari");

// Helper for SSR-safe layout effect
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const keyboardSingleton = (() => {
  // If not Safari, return a mock to avoid attaching listeners.
  if (!isSafari) {
    const mockInstance = {
      baselineVH: 0,
      currentVH: 0,
      isOpen: false,
    };
    return {
      getInstance: () => mockInstance,
      removeInstance: () => {}, // No-op
    };
  }

  let instance = null;
  let listenersAttached = false;

  function createInstance() {
    const state = {
      baselineVH: window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight,
      currentVH: window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight,
      isOpen: false,
    };

    const forceSafariFix = () => {
      // Double rAF ensures this runs in the next paint frame,
      // reducing layout thrashing.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(window.scrollX, window.scrollY);
        });
      });
    };

    const onResize = () => {
      if (!window.visualViewport) return;
      const vh = window.visualViewport.height;
      state.currentVH = vh;

      // If viewport shrinks significantly, we assume keyboard is open.
      // We DO NOT force scroll here to avoid flickering during animation.
      state.isOpen = vh < state.baselineVH - 150;
    };

    const onFocusOut = () => {
      // We only force the fix when the user stops typing/closes keyboard.
      // This prevents the page from getting stuck in a wrong offset
      // without causing flickering while typing.
      state.isOpen = false;
      forceSafariFix();
    };

    // Attach listeners only once
    if (!listenersAttached && window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize);
      window.addEventListener("focusout", onFocusOut, true);
      listenersAttached = true;
    }

    return {
      state,
      cleanup: () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", onResize);
        }
        window.removeEventListener("focusout", onFocusOut, true);
        listenersAttached = false;
        instance = null;
      },
    };
  }

  return {
    getInstance() {
      if (!instance) instance = createInstance();
      return instance.state;
    },
    removeInstance() {
      if (instance) {
        instance.cleanup();
      }
    },
  };
})();

const TouchMoveSingleton = (() => {
  function isScrollable(el) {
    if (!el) return false;

    // OPTIMIZATION: Check cheap properties first.
    // If content is smaller than container, it's definitely not scrollable.
    if (el.scrollHeight <= el.clientHeight) return false;

    // Expensive check (getComputedStyle causes reflow) runs last.
    const style = window.getComputedStyle(el);
    const oY = style.overflowY;
    return oY === "auto" || oY === "scroll";
  }

  const handleTouchMove = (e, config) => {
    const { enabled, justKeyboardFixes, enabledNormalMode } = config;

    // If event is not cancelable, ignore it to prevent console errors.
    if (!enabled || !e.cancelable) return;

    const keyboard = keyboardSingleton.getInstance();

    // Mode 1 — ONLY block when keyboard is open
    if (justKeyboardFixes) {
      if (keyboard.isOpen) e.preventDefault();
      return;
    }

    // Mode 2 — normal blocking system
    if (!enabledNormalMode) return;

    // If keyboard is open in normal mode, block strictly to prevent body jumps
    if (keyboard.isOpen) {
      e.preventDefault();
      return;
    }

    const target = e.target;

    // Check closest scrollable containers
    const scrollableContainer = target.closest(
      ".MuiDialogContent-root, .MuiInputBase-inputMultiline, .MuiDialog-root, .MuiDrawer-paper"
    );

    // If we are inside a scrollable element, allow the scroll (stopPropagation)
    if (scrollableContainer && isScrollable(scrollableContainer)) {
      // Stop propagation to ensure body doesn't receive the move event
      e.stopPropagation();
      return;
    }

    e.preventDefault();
  };

  // We bind the listener once and reuse the function reference
  return {
    getListener: () => handleTouchMove,
    mainNode: typeof document !== "undefined" ? document.body : null,
  };
})();

/**
 * Optimized hook for scroll blocking and Safari keyboard handling.
 */
let globalLockCount = 0;
let globalOriginalOverflow = "";

/**
 * Optimized hook for scroll blocking and Safari keyboard handling.
 */
export const useModalScrollBlocker = ({
  open,
  enabled = true,
  justKeyboardFixes = false,
}) => {
  const { getListener, mainNode } = TouchMoveSingleton;

  // 1. Init Keyboard Singleton
  useEffect(() => {
    keyboardSingleton.getInstance();
    // Usually we don't clean up the singleton aggressively if used often
  }, []);

  // 2. Handle Scroll Blocking & Listeners
  // Using useLayoutEffect prevents visual flickering when changing overflow
  useIsomorphicLayoutEffect(() => {
    if (!mainNode || !enabled) return;

    const config = {
      enabled,
      justKeyboardFixes,
      enabledNormalMode: !justKeyboardFixes,
    };

    // Create a stable listener reference
    const handler = (e) => getListener()(e, config);

    const shouldLock = !justKeyboardFixes && open;
    const shouldListenOnly = justKeyboardFixes && open;

    if (shouldLock) {
      if (globalLockCount === 0) {
        globalOriginalOverflow = mainNode.style.overflow;
        mainNode.style.overflow = "hidden";
      }
      globalLockCount++;
      // Add touchmove listener (non-passive to allow preventDefault)
      mainNode.addEventListener("touchmove", handler, { passive: false });
    } else if (shouldListenOnly) {
      // Only apply listener, do not modify body overflow
      mainNode.addEventListener("touchmove", handler, { passive: false });
    }

    // Cleanup function
    return () => {
      if (shouldLock) {
        globalLockCount--;
        if (globalLockCount === 0) {
          mainNode.style.overflow = globalOriginalOverflow;
          globalOriginalOverflow = "";
        }
        mainNode.removeEventListener("touchmove", handler);
      }
      if (shouldListenOnly) {
        mainNode.removeEventListener("touchmove", handler);
      }
    };
  }, [open, enabled, justKeyboardFixes, mainNode, getListener]);
};
